import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import AdUnit from '@/components/AdUnit';
import AdSidebarStack from '@/components/AdSidebarStack';
import { supabase } from '@/lib/supabase';
import { usePageMeta } from '@/hooks/usePageMeta';

const SIDEBAR_SLOT = import.meta.env.VITE_AD_SLOT_SIDEBAR as string | undefined;
const INCONTENT_SLOT = import.meta.env.VITE_AD_SLOT_INCONTENT as string | undefined;

/** Renders `**bold**` markdown runs as <strong>. The local model sometimes uses this convention. */
function renderInlineMarkdown(text: string) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}

interface Guide {
  id: string;
  title: string;
  summary: string;
  body: string | null;
  tag: string;
  read_time: string;
  author: string;
  image: string;
  created_at: string;
}

interface FeaturedProduct {
  id: string;
  name: string;
  brand: string;
  price: number;
  original_price: number | null;
  rating: number;
  image: string;
}

export default function GuideDetail() {
  const { id } = useParams<{ id: string }>();
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [guide, setGuide] = useState<Guide | null>(null);
  const [more, setMore] = useState<Guide[]>([]);
  const [featured, setFeatured] = useState<FeaturedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  usePageMeta(guide?.title, guide?.summary);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data, error: fetchError } = await supabase.from('buying_guides').select('*').eq('id', id).maybeSingle();
        if (fetchError) throw fetchError;
        if (!data) { setError('Guide not found'); setLoading(false); return; }
        setGuide(data);
        const { data: moreData } = await supabase.from('buying_guides').select('*').neq('id', id).order('created_at', { ascending: false }).limit(3);
        setMore(moreData || []);
        const { data: linkedData } = await supabase
          .from('guide_products')
          .select('sort_order, products(id,name,brand,price,original_price,rating,image)')
          .eq('guide_id', id)
          .order('sort_order');
        if (linkedData) {
          setFeatured(linkedData.map((row) => row.products as unknown as FeaturedProduct).filter(Boolean));
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load guide');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-ink-900 antialiased">
        <Header />
        <main className="pt-24 md:pt-32 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center text-ink-400 text-sm py-20">Loading guide...</div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !guide) {
    return (
      <div className="min-h-screen bg-white text-ink-900 antialiased">
        <Header />
        <main className="pt-24 md:pt-32 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <p className="text-rose-600 text-sm mb-3">{error || 'Guide not found'}</p>
            <Link to="/guides" className="h-9 px-4 rounded-lg bg-ink-900 text-white text-sm font-semibold whitespace-nowrap inline-flex items-center">Browse Guides</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const paragraphs = (guide.body && guide.body.trim() ? guide.body : guide.summary)
    .split(/\n{2,}/)
    .map(p => p.trim())
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white text-ink-900 antialiased">
      <Header />
      <main className="pt-24 md:pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="lg:grid lg:grid-cols-[1fr_300px] lg:gap-10 lg:items-start">
        <div className="max-w-3xl" ref={mainContentRef}>
          <nav className="flex items-center gap-2 text-sm text-ink-500 mb-6">
            <Link to="/" className="hover:text-cta-600 transition whitespace-nowrap">Home</Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-base"></i>
            </span>
            <Link to="/guides" className="hover:text-cta-600 transition whitespace-nowrap">Buying Guides</Link>
          </nav>

          <span className="inline-flex items-center h-6 px-2.5 rounded-md bg-cta-50 text-cta-700 text-[11px] font-bold uppercase tracking-wider">
            {guide.tag}
          </span>
          <h1 className="mt-3 font-display text-3xl md:text-5xl font-bold text-ink-900 leading-tight tracking-tight">
            {guide.title}
          </h1>
          <div className="mt-4 flex items-center gap-3 text-sm text-ink-500">
            <span className="font-medium text-ink-700">{guide.author}</span>
            <span>&middot;</span>
            <span>{new Date(guide.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
            <span>&middot;</span>
            <span>{guide.read_time}</span>
          </div>

          <div className="mt-8 aspect-[16/9] rounded-2xl overflow-hidden bg-ink-50">
            <img src={guide.image} alt={guide.title} className="w-full h-full object-contain p-8" />
          </div>

          <article className="mt-10 space-y-5 text-ink-700 leading-relaxed text-[17px]">
            {paragraphs.map((p, i) => (
              <div key={i}>
                {p.startsWith('### ') ? (
                  <h2 className="!mt-10 font-display text-2xl font-bold text-ink-900 tracking-tight">
                    {p.slice(4)}
                  </h2>
                ) : (
                  <p>{renderInlineMarkdown(p)}</p>
                )}
                {i === Math.floor(paragraphs.length / 2) && (
                  <AdUnit slot={INCONTENT_SLOT} label="Advertisement" className="my-8" style={{ minHeight: 250 }} />
                )}
              </div>
            ))}
          </article>

          <AdUnit slot={INCONTENT_SLOT} label="Advertisement" className="mt-8" style={{ minHeight: 250 }} />

          {featured.length > 0 && (
            <div className="mt-12 p-6 md:p-8 rounded-2xl bg-ink-50 border border-ink-100">
              <p className="text-xs font-semibold uppercase tracking-wider text-cta-600 mb-5">Featured in this guide</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {featured.map((p) => (
                  <Link
                    key={p.id}
                    to={`/product/${p.id}`}
                    className="group bg-white rounded-xl border border-ink-100 hover:border-cta-300 hover:shadow-card transition p-4 flex flex-col"
                  >
                    <div className="aspect-square rounded-lg bg-ink-50 overflow-hidden mb-3">
                      <img src={p.image} alt={p.name} className="w-full h-full object-contain p-3 group-hover:scale-105 transition duration-300" />
                    </div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-400">{p.brand}</p>
                    <p className="mt-1 text-sm font-semibold text-ink-900 leading-snug line-clamp-2">{p.name}</p>
                    <div className="mt-2 flex items-center gap-1.5">
                      <i className="ri-star-fill text-amber-500 text-xs"></i>
                      <span className="text-xs font-semibold text-ink-800">{p.rating}</span>
                    </div>
                    <div className="mt-auto pt-3 flex items-center justify-between">
                      <span className="font-display text-lg font-bold text-cta-600">${p.price}</span>
                      <span className="text-xs font-semibold text-ink-700 group-hover:text-cta-600 transition flex items-center gap-1">
                        View <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="hidden lg:block">
          <AdSidebarStack slot={SIDEBAR_SLOT} targetRef={mainContentRef} />
        </aside>
        </div>
        </div>

        {more.length > 0 && (
          <section className="max-w-5xl mx-auto px-4 md:px-6 mt-16">
            <h2 className="font-display text-2xl font-bold text-ink-900 tracking-tight mb-6">More buying guides</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {more.map(g => (
                <Link
                  key={g.id}
                  to={`/guides/${g.id}`}
                  className="group relative overflow-hidden rounded-2xl bg-white border border-ink-100 shadow-soft hover:-translate-y-1 transition duration-300"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-ink-50">
                    <img src={g.image} alt={g.title} className="w-full h-full object-contain p-4 group-hover:scale-105 transition duration-700" />
                  </div>
                  <div className="p-5">
                    <span className="inline-flex items-center h-5 px-1.5 rounded bg-cta-50 text-cta-700 text-[10px] font-bold uppercase tracking-wider">
                      {g.tag}
                    </span>
                    <h3 className="mt-2 font-semibold text-ink-900 leading-snug line-clamp-2">{g.title}</h3>
                    <p className="mt-2 text-xs text-ink-400">{g.author} &middot; {g.read_time}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
